import { useState } from "react";

type Props = {
  value: unknown;
  /** Initial open depth — branches at depth <= initialDepth start expanded. */
  initialDepth?: number;
};

export default function JsonViewer({ value, initialDepth = 1 }: Props) {
  return (
    <div
      style={{
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: ".78rem",
        lineHeight: 1.45,
        color: "#e5e7eb",
        background: "#0f172a",
        padding: "0.75rem 0.9rem",
        borderRadius: "0.45rem",
        overflowX: "auto",
      }}
    >
      <Node value={value} depth={0} initialDepth={initialDepth} />
    </div>
  );
}

function Node({
  value,
  depth,
  initialDepth,
  trailingComma = false,
}: {
  value: unknown;
  depth: number;
  initialDepth: number;
  trailingComma?: boolean;
}) {
  if (value === null) return <Primitive text="null" color="#9ca3af" trailingComma={trailingComma} />;
  if (value === undefined)
    return <Primitive text="undefined" color="#9ca3af" trailingComma={trailingComma} />;
  if (typeof value === "string")
    return (
      <Primitive
        text={JSON.stringify(value)}
        color="#a7f3d0"
        trailingComma={trailingComma}
      />
    );
  if (typeof value === "number")
    return (
      <Primitive
        text={String(value)}
        color="#fcd34d"
        trailingComma={trailingComma}
      />
    );
  if (typeof value === "boolean")
    return (
      <Primitive
        text={String(value)}
        color="#fca5a5"
        trailingComma={trailingComma}
      />
    );
  if (Array.isArray(value))
    return (
      <Collection
        opener="["
        closer="]"
        entries={value.map((v, i) => [String(i), v] as const)}
        depth={depth}
        initialDepth={initialDepth}
        trailingComma={trailingComma}
        showKey={false}
      />
    );
  if (typeof value === "object")
    return (
      <Collection
        opener="{"
        closer="}"
        entries={Object.entries(value as Record<string, unknown>)}
        depth={depth}
        initialDepth={initialDepth}
        trailingComma={trailingComma}
        showKey={true}
      />
    );
  return (
    <Primitive
      text={String(value)}
      color="#e5e7eb"
      trailingComma={trailingComma}
    />
  );
}

function Primitive({
  text,
  color,
  trailingComma,
}: {
  text: string;
  color: string;
  trailingComma: boolean;
}) {
  return (
    <span style={{ color }}>
      {text}
      {trailingComma && <span style={{ color: "#9ca3af" }}>,</span>}
    </span>
  );
}

function Collection({
  opener,
  closer,
  entries,
  depth,
  initialDepth,
  trailingComma,
  showKey,
}: {
  opener: string;
  closer: string;
  entries: ReadonlyArray<readonly [string, unknown]>;
  depth: number;
  initialDepth: number;
  trailingComma: boolean;
  showKey: boolean;
}) {
  const [open, setOpen] = useState(depth <= initialDepth);

  if (entries.length === 0) {
    return (
      <span style={{ color: "#9ca3af" }}>
        {opener}
        {closer}
        {trailingComma && ","}
      </span>
    );
  }

  if (!open) {
    return (
      <span>
        <Toggle open={false} onClick={() => setOpen(true)} />
        <span style={{ color: "#9ca3af" }}>{opener}</span>
        <span
          style={{ color: "#6b7280", marginLeft: 4, marginRight: 4 }}
          title={`${entries.length} item${entries.length === 1 ? "" : "s"}`}
        >
          …
        </span>
        <span style={{ color: "#9ca3af" }}>
          {closer}
          {trailingComma && ","}
        </span>
        <span style={{ color: "#6b7280", marginLeft: 6 }}>
          {entries.length} item{entries.length === 1 ? "" : "s"}
        </span>
      </span>
    );
  }

  const indent = "  ".repeat(depth + 1);
  const closerIndent = "  ".repeat(depth);

  return (
    <span>
      <Toggle open onClick={() => setOpen(false)} />
      <span style={{ color: "#9ca3af" }}>{opener}</span>
      {entries.map(([key, val], idx) => {
        const last = idx === entries.length - 1;
        return (
          <div key={`${key}-${idx}`}>
            <span style={{ whiteSpace: "pre" }}>{indent}</span>
            {showKey && (
              <>
                <span style={{ color: "#93c5fd" }}>{JSON.stringify(key)}</span>
                <span style={{ color: "#9ca3af" }}>: </span>
              </>
            )}
            <Node
              value={val}
              depth={depth + 1}
              initialDepth={initialDepth}
              trailingComma={!last}
            />
          </div>
        );
      })}
      <div>
        <span style={{ whiteSpace: "pre" }}>{closerIndent}</span>
        <span style={{ color: "#9ca3af" }}>
          {closer}
          {trailingComma && ","}
        </span>
      </div>
    </span>
  );
}

function Toggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "transparent",
        color: "#9ca3af",
        border: "none",
        padding: 0,
        marginRight: 4,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: ".75rem",
      }}
      aria-label={open ? "Colapsar" : "Expandir"}
    >
      {open ? "▾" : "▸"}
    </button>
  );
}
